import { useState, useRef, useEffect } from 'react'
import { Bell, Check, Sparkles, AlertCircle, CheckCircle2, Radar, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { useUserNotifications } from '@/hooks/useUserNotifications'
import { Spinner } from '@/design-system/feedback'
import { NotificationBadge } from './NotificationBadge'
import '@/design-system/shell.css'

/**
 * Notification dropdown component that displays user notifications
 * Includes beta access grants, feature announcements, and system notifications
 */
export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    isMarkingAsRead
  } = useUserNotifications()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'beta_access_granted':
        return <Sparkles aria-hidden="true" />
      case 'feature_announcement':
        return <Bell aria-hidden="true" />
      case 'system':
        return <AlertCircle aria-hidden="true" />
      case 'scan_complete':
        return <CheckCircle2 aria-hidden="true" />
      case 'drift_detected':
        return <Radar aria-hidden="true" />
      case 'chief_nudge':
        return <TrendingUp aria-hidden="true" />
      default:
        return <Bell aria-hidden="true" />
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ds-trigger ds-trigger--icon"
        aria-label={`Notifications (${unreadCount} unread)`}
      >
        <Bell aria-hidden="true" />
        {unreadCount > 0 && (
          <div className="absolute top-0 right-0">
            <NotificationBadge count={unreadCount} />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="ds-drop ds-drop--lg">
          {/* Header */}
          <div className="ds-drop__head">
            <div className="ds-drop__what">
              <Bell aria-hidden="true" />
              <h3 className="ds-drop__name">Notifications</h3>
              {unreadCount > 0 && (
                <NotificationBadge count={unreadCount} />
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                disabled={isMarkingAsRead}
                className="ds-notif__all ds-small"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center" role="status" aria-live="polite">
                <Spinner quiet className="mx-auto" />
                <p className="ds-small ds-muted mt-2">Finding your notifications</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="ds-notif__none" aria-hidden="true" />
                <p className="ds-small ds-muted">Nothing to tell you yet</p>
              </div>
            ) : (
              <div className="ds-notif__list">
                {notifications.map((notification: any) => (
                  <div
                    key={notification.id}
                    className={cn('ds-notif__row', !notification.is_read && 'ds-notif__row--new')}
                  >
                    <div className="flex items-start gap-3">
                      <div className="ds-notif__mark">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium">{notification.title}</p>
                          {!notification.is_read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="ds-notif__tick"
                              title="Mark as read"
                            >
                              <Check aria-hidden="true" />
                            </button>
                          )}
                        </div>
                        <p className="ds-small ds-muted mt-1">{notification.message}</p>
                        <p className="ds-small ds-faint mt-2">
                          {new Date(notification.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                        {notification.type === 'beta_access_granted' && (
                          <Link
                            to="/ai-visibility"
                            className="ds-link ds-small"
                            onClick={() => {
                              setIsOpen(false)
                              if (!notification.is_read) {
                                markAsRead(notification.id)
                              }
                            }}
                          >
                            View AI Analytics →
                          </Link>
                        )}
                        {notification.type === 'scan_complete' && notification.metadata?.site_id && (
                          <Link
                            to={`/ai-analytics/site-score?site=${notification.metadata.site_id}`}
                            className="ds-link ds-small"
                            onClick={() => {
                              setIsOpen(false)
                              if (!notification.is_read) {
                                markAsRead(notification.id)
                              }
                            }}
                          >
                            View Results →
                          </Link>
                        )}
                        {notification.type === 'drift_detected' && notification.metadata?.domain_slug && (
                          <Link
                            to={`/domains/${encodeURIComponent(notification.metadata.domain_slug)}/monitoring${
                              notification.metadata.url_id
                                ? `?url=${encodeURIComponent(notification.metadata.url_id)}`
                                : ''
                            }`}
                            className="ds-link ds-small"
                            onClick={() => {
                              setIsOpen(false)
                              if (!notification.is_read) {
                                markAsRead(notification.id)
                              }
                            }}
                          >
                            Review changes →
                          </Link>
                        )}
                        {notification.type === 'chief_nudge' && (
                          <Link
                            to="/chief"
                            className="ds-link ds-small"
                            onClick={() => {
                              setIsOpen(false)
                              if (!notification.is_read) {
                                markAsRead(notification.id)
                              }
                            }}
                          >
                            Review in the chief →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="ds-drop__foot">
              <button
                onClick={() => setIsOpen(false)}
                className="ds-notif__close"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
